(function (global) {
  'use strict';

  if (global.NoxPageTransition && typeof global.NoxPageTransition.navigate === 'function') return;

  const STORAGE_KEY = 'nox_page_chaos_payload_v2';
  const MAX_AGE_MS = 15000;
  const MIN_DURATION = 900;

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const easeInOutCubic = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const easeOutQuint = t => 1 - Math.pow(1 - t, 5);
  const pulseAt = (progress, center, width) => {
    const distance = Math.abs(progress - center);
    if (distance >= width) return 0;
    const t = 1 - (distance / width);
    return t * t * (3 - (2 * t));
  };

  function normalizeUrl(url) {
    try {
      const parsed = new URL(url, global.location.href);
      return `${parsed.pathname}${parsed.search}`;
    } catch (_) {
      return String(url || '');
    }
  }

  function mulberry32(seed) {
    let t = seed >>> 0;
    return function () {
      t += 0x6D2B79F5;
      let result = Math.imul(t ^ (t >>> 15), t | 1);
      result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
      return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
    };
  }

  function resolveSettings(raw) {
    const config = raw || {};
    return {
      duration: Math.max(MIN_DURATION, parseInt(config.duration, 10) || 1500),
      intensity: clamp(Number(config.intensity ?? 0.7), 0, 1),
      chaos: clamp(Number(config.chaos ?? 0.6), 0, 1),
      blur: clamp(Number(config.blur ?? 6), 0, 16),
      reverseFlash: Boolean(config.reverseFlash ?? true),
      flashIntensity: clamp(Number(config.flashIntensity ?? 0.4), 0, 1),
      flashCount: clamp(parseInt(config.flashCount, 10) || 2, 0, 4)
    };
  }

  class NoxPageTransition {
    constructor() {
      this.overlay = null;
      this.canvas = null;
      this.ctx = null;
      this.echo = null;
      this.prism = null;
      this.flash = null;
      this.veil = null;
      this.grain = null;
      this._raf = 0;
      this._job = null;
      this._imageCache = new Map();
      this._reducedMotion = global.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      this._lowPower = (global.navigator?.hardwareConcurrency || 8) <= 4;
      this._dpr = 1;
      this._viewWidth = 0;
      this._viewHeight = 0;
      this._boundResize = () => this._resizeCanvas();

      this._mount();
      this._playIncoming();
      global.addEventListener('resize', this._boundResize);
    }

    _mount() {
      this.overlay = document.querySelector('.nox-transition-overlay');
      if (!this.overlay) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'nox-transition-overlay';
        this.overlay.setAttribute('aria-hidden', 'true');
        this.overlay.innerHTML = [
          '<canvas class="nox-transition-overlay__canvas"></canvas>',
          '<div class="nox-transition-overlay__echo"></div>',
          '<div class="nox-transition-overlay__prism"></div>',
          '<div class="nox-transition-overlay__flash"></div>',
          '<div class="nox-transition-overlay__veil"></div>',
          '<div class="nox-transition-overlay__grain"></div>'
        ].join('');
        document.body.appendChild(this.overlay);
      }

      this.canvas = this.overlay.querySelector('.nox-transition-overlay__canvas');
      this.ctx = this.canvas?.getContext('2d', { alpha: true, desynchronized: true }) || null;
      this.echo = this.overlay.querySelector('.nox-transition-overlay__echo');
      this.prism = this.overlay.querySelector('.nox-transition-overlay__prism');
      this.flash = this.overlay.querySelector('.nox-transition-overlay__flash');
      this.veil = this.overlay.querySelector('.nox-transition-overlay__veil');
      this.grain = this.overlay.querySelector('.nox-transition-overlay__grain');

      this._resizeCanvas();
      this._resetVisualState();
    }

    _resizeCanvas() {
      if (!this.canvas || !this.ctx) return;
      const width = Math.max(global.innerWidth || document.documentElement.clientWidth || 1, 1);
      const height = Math.max(global.innerHeight || document.documentElement.clientHeight || 1, 1);
      const isMobile = width <= 767;
      this._dpr = Math.min(global.devicePixelRatio || 1, this._lowPower || isMobile ? 1.15 : 1.5);
      this._viewWidth = width;
      this._viewHeight = height;
      this.canvas.width = Math.max(1, Math.round(width * this._dpr));
      this.canvas.height = Math.max(1, Math.round(height * this._dpr));
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
      this.ctx.setTransform(this._dpr, 0, 0, this._dpr, 0, 0);
      this.ctx.imageSmoothingEnabled = true;
    }

    _resetVisualState() {
      if (!this.overlay) return;
      this.overlay.style.setProperty('--nox-morph-canvas-opacity', '1');
      this.overlay.style.setProperty('--nox-morph-echo-opacity', '0');
      this.overlay.style.setProperty('--nox-morph-prism-opacity', '0');
      this.overlay.style.setProperty('--nox-morph-echo-x', '0px');
      this.overlay.style.setProperty('--nox-morph-echo-y', '0px');
      this.overlay.style.setProperty('--nox-morph-prism-x', '0px');
      this.overlay.style.setProperty('--nox-morph-prism-y', '0px');
      this.overlay.style.setProperty('--nox-morph-echo-scale', '1');
      this.overlay.style.setProperty('--nox-morph-prism-scale', '1');
      this.overlay.style.setProperty('--nox-morph-echo-rotate', '0deg');
      this.overlay.style.setProperty('--nox-morph-prism-rotate', '0deg');
      this.overlay.style.setProperty('--nox-morph-prism-split', '0px');
      this.overlay.style.setProperty('--nox-morph-blur', '0px');
      this.overlay.style.setProperty('--nox-morph-echo-sat', '1.05');
      this.overlay.style.setProperty('--nox-morph-prism-sat', '1.12');
      this.overlay.style.setProperty('--nox-morph-flash-opacity', '0');
      this.overlay.style.setProperty('--nox-morph-flash-scale', '1');
      this.overlay.style.setProperty('--nox-morph-flash-rotate', '0deg');
      this.overlay.style.setProperty('--nox-morph-veil-opacity', '0');
      this.overlay.style.setProperty('--nox-morph-grain-opacity', '0');
      if (this.echo) this.echo.style.backgroundImage = 'none';
      if (this.prism) this.prism.style.backgroundImage = 'none';
      if (this.ctx) this.ctx.clearRect(0, 0, this._viewWidth, this._viewHeight);
    }

    _setTextureLayers(textureUrl) {
      if (!this.echo || !this.prism) return;
      if (!textureUrl) {
        this.echo.style.backgroundImage = 'none';
        this.prism.style.backgroundImage = 'none';
        return;
      }

      const cssTexture = `url("${textureUrl.replace(/["\\\n\r]/g, '\\$&')}")`;
      this.echo.style.backgroundImage = cssTexture;
      this.prism.style.backgroundImage = cssTexture;
    }

    _cleanup() {
      if (this._raf) {
        global.cancelAnimationFrame(this._raf);
        this._raf = 0;
      }
      this._job = null;
      if (!this.overlay) return;
      this.overlay.classList.remove('is-active');
      this._resetVisualState();
    }

    _getCurrentRoot() {
      return document.querySelector('.nox-room, .nox-hall') || document.body;
    }

    _getSourceElement(root) {
      if (!(root instanceof Element)) return null;
      return root.querySelector(
        '.nox-room__bg--behind, .nox-hall__bg--behind, .nox-room__bg, .nox-hall__bg, picture img, img, .nox-room__video, .nox-hall__video, video'
      );
    }

    _extractTexture(root) {
      const source = this._getSourceElement(root);
      if (source instanceof HTMLImageElement) return source.currentSrc || source.src || '';
      if (source instanceof HTMLVideoElement) return source.getAttribute('poster') || '';
      return '';
    }

    _getDrawable(root, textureUrl) {
      const source = this._getSourceElement(root);
      if (source instanceof HTMLImageElement && source.complete && source.naturalWidth > 0) return source;
      if (source instanceof HTMLVideoElement && source.readyState >= 2 && source.videoWidth > 0) return source;
      if (!textureUrl) return null;

      let entry = this._imageCache.get(textureUrl);
      if (!entry) {
        const img = new Image();
        img.decoding = 'async';
        entry = { img, ready: false };
        img.onload = () => { entry.ready = true; };
        img.onerror = () => { entry.ready = false; };
        img.src = textureUrl;
        this._imageCache.set(textureUrl, entry);
      }
      return entry.ready ? entry.img : null;
    }

    _buildStrips(settings, seed) {
      const rand = mulberry32(seed);
      const mobileBoost = this._viewWidth <= 767 ? 0.8 : 1;
      const powerBoost = this._lowPower ? 0.78 : 1;
      const countFactor = mobileBoost * powerBoost;
      const verticalCount = Math.max(4, Math.round((6 + settings.chaos * 6) * countFactor));
      const horizontalCount = Math.max(2, Math.round((2 + settings.chaos * 4) * countFactor));

      const vertical = Array.from({ length: verticalCount }, (_, index) => ({
        start: index / verticalCount,
        span: 1 / verticalCount,
        drift: (rand() * 2) - 1,
        lift: (rand() * 2) - 1,
        zoom: 0.78 + rand() * 0.48,
        alpha: 0.72 + rand() * 0.45,
        phase: rand() * Math.PI * 2
      }));

      const horizontal = Array.from({ length: horizontalCount }, (_, index) => ({
        start: index / horizontalCount,
        span: 1 / horizontalCount,
        drift: (rand() * 2) - 1,
        lift: (rand() * 2) - 1,
        zoom: 0.8 + rand() * 0.44,
        alpha: 0.58 + rand() * 0.42,
        phase: rand() * Math.PI * 2
      }));

      return { vertical, horizontal };
    }

    _getIntrinsicSize(drawable) {
      if (drawable instanceof HTMLVideoElement) {
        return {
          width: drawable.videoWidth || drawable.clientWidth || 1,
          height: drawable.videoHeight || drawable.clientHeight || 1
        };
      }
      return {
        width: drawable.naturalWidth || drawable.width || 1,
        height: drawable.naturalHeight || drawable.height || 1
      };
    }

    _getCoverCrop(drawable, targetWidth, targetHeight) {
      const source = this._getIntrinsicSize(drawable);
      const sourceRatio = source.width / Math.max(source.height, 1);
      const targetRatio = targetWidth / Math.max(targetHeight, 1);

      let width = source.width;
      let height = source.height;
      let x = 0;
      let y = 0;

      if (sourceRatio > targetRatio) {
        width = source.height * targetRatio;
        x = (source.width - width) * 0.5;
      } else {
        height = source.width / Math.max(targetRatio, 0.01);
        y = (source.height - height) * 0.5;
      }

      return { x, y, width, height };
    }

    _drawBase(drawable, crop, state) {
      if (!this.ctx) return;
      const drawWidth = this._viewWidth * state.baseScale;
      const drawHeight = this._viewHeight * state.baseScale;
      const drawX = ((this._viewWidth - drawWidth) * 0.5) + state.baseX;
      const drawY = ((this._viewHeight - drawHeight) * 0.5) + state.baseY;

      this.ctx.save();
      this.ctx.globalAlpha = state.baseAlpha;
      this.ctx.drawImage(
        drawable,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        drawX,
        drawY,
        drawWidth,
        drawHeight
      );
      this.ctx.restore();
    }

    _drawSlices(drawable, crop, strips, state, vertical) {
      if (!this.ctx || !strips.length) return;
      this.ctx.save();
      this.ctx.globalAlpha = state.alpha;

      strips.forEach(strip => {
        if (vertical) {
          const sliceSourceX = crop.x + (crop.width * strip.start);
          const sliceSourceWidth = crop.width * strip.span;
          const baseWidth = this._viewWidth * strip.span;
          const sway = Math.sin(state.phase + strip.phase) * state.amplitude * strip.drift;
          const lift = Math.cos((state.phase * 1.08) + strip.phase) * state.lift * strip.lift;
          const drawWidth = baseWidth * (1 + (state.scale * strip.zoom));
          const drawHeight = this._viewHeight * (1 + (state.scale * 0.26));
          const drawX = (this._viewWidth * strip.start) + sway - ((drawWidth - baseWidth) * 0.5);
          const drawY = ((this._viewHeight - drawHeight) * 0.5) + lift;

          this.ctx.globalAlpha = state.alpha * strip.alpha;
          this.ctx.drawImage(
            drawable,
            sliceSourceX,
            crop.y,
            sliceSourceWidth,
            crop.height,
            drawX,
            drawY,
            drawWidth,
            drawHeight
          );
          return;
        }

        const sliceSourceY = crop.y + (crop.height * strip.start);
        const sliceSourceHeight = crop.height * strip.span;
        const baseHeight = this._viewHeight * strip.span;
        const sway = Math.sin(state.phase + strip.phase) * state.amplitude * strip.drift;
        const lift = Math.cos((state.phase * 1.16) + strip.phase) * state.lift * strip.lift;
        const drawWidth = this._viewWidth * (1 + (state.scale * 0.24));
        const drawHeight = baseHeight * (1 + (state.scale * strip.zoom));
        const drawX = ((this._viewWidth - drawWidth) * 0.5) + sway;
        const drawY = (this._viewHeight * strip.start) + lift - ((drawHeight - baseHeight) * 0.5);

        this.ctx.globalAlpha = state.alpha * strip.alpha;
        this.ctx.drawImage(
          drawable,
          crop.x,
          sliceSourceY,
          crop.width,
          sliceSourceHeight,
          drawX,
          drawY,
          drawWidth,
          drawHeight
        );
      });

      this.ctx.restore();
    }

    _applyCssState(state) {
      if (!this.overlay) return;
      this.overlay.style.setProperty('--nox-morph-canvas-opacity', state.canvasOpacity.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-echo-opacity', state.echoOpacity.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-prism-opacity', state.prismOpacity.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-echo-x', `${state.echoX.toFixed(2)}px`);
      this.overlay.style.setProperty('--nox-morph-echo-y', `${state.echoY.toFixed(2)}px`);
      this.overlay.style.setProperty('--nox-morph-prism-x', `${state.prismX.toFixed(2)}px`);
      this.overlay.style.setProperty('--nox-morph-prism-y', `${state.prismY.toFixed(2)}px`);
      this.overlay.style.setProperty('--nox-morph-echo-scale', state.echoScale.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-prism-scale', state.prismScale.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-echo-rotate', `${state.echoRotate.toFixed(3)}deg`);
      this.overlay.style.setProperty('--nox-morph-prism-rotate', `${state.prismRotate.toFixed(3)}deg`);
      this.overlay.style.setProperty('--nox-morph-prism-split', `${state.prismSplit.toFixed(2)}px`);
      this.overlay.style.setProperty('--nox-morph-blur', `${state.blur.toFixed(2)}px`);
      this.overlay.style.setProperty('--nox-morph-echo-sat', state.echoSaturate.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-prism-sat', state.prismSaturate.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-flash-opacity', state.flashOpacity.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-flash-scale', state.flashScale.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-flash-rotate', `${state.flashRotate.toFixed(3)}deg`);
      this.overlay.style.setProperty('--nox-morph-veil-opacity', state.veilOpacity.toFixed(4));
      this.overlay.style.setProperty('--nox-morph-grain-opacity', state.grainOpacity.toFixed(4));
    }

    _computeReverseFlash(progress, settings, mode) {
      if (!settings.reverseFlash || settings.flashIntensity <= 0 || settings.flashCount <= 0) {
        return {
          pulse: 0,
          signed: 0,
          drift: 0,
          opacity: 0,
          scale: 1,
          rotate: 0
        };
      }

      const count = settings.flashCount;
      const start = mode === 'out' ? 0.18 : 0.12;
      const span = mode === 'out' ? 0.52 : 0.42;
      const width = 0.055 + ((1 - settings.flashIntensity) * 0.025);
      let pulse = 0;
      let signed = 0;

      for (let index = 0; index < count; index += 1) {
        const center = start + (span * (index / Math.max(count - 1, 1)));
        const peak = pulseAt(progress, center, width);
        if (!peak) continue;
        pulse = Math.max(pulse, peak);
        signed += peak * (index % 2 === 0 ? -1 : 1);
      }

      const energy = pulse * settings.flashIntensity;
      const direction = clamp(signed, -1, 1);

      return {
        pulse: energy,
        signed: direction,
        drift: direction * energy * (10 + settings.chaos * 18),
        opacity: energy * (0.16 + settings.flashIntensity * 0.26),
        scale: 1 + (energy * 0.12),
        rotate: direction * energy * 1.4
      };
    }

    _computeOutgoing(progress, settings) {
      const eased = easeInOutCubic(progress);
      const wave = Math.sin(clamp(progress / 0.82, 0, 1) * Math.PI);
      const drift = Math.sin(progress * Math.PI * (1.5 + settings.chaos * 1.8));
      const pulse = Math.sin(progress * Math.PI * (3.8 + settings.chaos * 4.6));
      const amplitude = wave * (10 + settings.intensity * 24 + settings.chaos * 34);
      const flash = this._computeReverseFlash(progress, settings, 'out');
      const reverseFactor = 1 - (flash.pulse * 2.45);
      const warpedAmplitude = amplitude * reverseFactor;
      const reverseDrift = flash.drift;

      return {
        canvasOpacity: 1,
        baseAlpha: 1,
        baseScale: 1 + eased * (0.014 + settings.intensity * 0.04),
        baseX: (drift * (3 + settings.chaos * 8)) + reverseDrift,
        baseY: Math.cos(progress * Math.PI * 1.08) * (2 + settings.chaos * 5),
        primary: {
          amplitude: warpedAmplitude,
          lift: (drift * (4 + settings.chaos * 10)) - (reverseDrift * 0.45),
          scale: wave * (0.05 + settings.intensity * 0.05),
          alpha: 0.16 + wave * (0.22 + settings.intensity * 0.16),
          phase: progress * (4.2 + settings.chaos * 5.4)
        },
        secondary: {
          amplitude: amplitude * 0.58 * (1 - (flash.pulse * 2.1)),
          lift: (pulse * (2.5 + settings.chaos * 6)) + (reverseDrift * 0.28),
          scale: wave * (0.04 + settings.intensity * 0.04),
          alpha: wave * (0.08 + settings.chaos * 0.14),
          phase: progress * (5.1 + settings.chaos * 6.2)
        },
        echoOpacity: 0.08 + wave * (0.12 + settings.intensity * 0.12),
        prismOpacity: wave * (0.10 + settings.intensity * 0.15 + settings.chaos * 0.10),
        echoX: (warpedAmplitude * -0.18) - (reverseDrift * 0.4),
        echoY: drift * (2 + settings.chaos * 4),
        prismX: (warpedAmplitude * 0.16) + (reverseDrift * 0.55),
        prismY: pulse * (1.2 + settings.chaos * 2.8),
        echoScale: 1.01 + wave * (0.018 + settings.intensity * 0.02),
        prismScale: 1.01 + wave * (0.026 + settings.intensity * 0.026),
        echoRotate: drift * (0.12 + settings.chaos * 0.36),
        prismRotate: (pulse * (0.08 + settings.chaos * 0.28)) + flash.rotate,
        prismSplit: 0.8 + wave * (1.4 + settings.intensity * 2 + settings.chaos * 3.6) + (flash.pulse * (2 + settings.chaos * 4)),
        blur: settings.blur * (0.12 + wave * 0.52),
        echoSaturate: 1.02 + wave * (0.10 + settings.intensity * 0.18),
        prismSaturate: 1.08 + wave * (0.14 + settings.intensity * 0.22),
        flashOpacity: flash.opacity,
        flashScale: flash.scale,
        flashRotate: flash.rotate,
        veilOpacity: eased * (0.06 + settings.intensity * 0.10) + (flash.opacity * 0.7),
        grainOpacity: 0.05 + wave * (0.04 + settings.chaos * 0.05)
      };
    }

    _computeIncoming(progress, settings) {
      const clear = easeOutQuint(progress);
      const residue = 1 - clear;
      const arc = Math.sin(progress * Math.PI);
      const drift = Math.sin(progress * Math.PI * (1.2 + settings.chaos * 1.3));
      const amplitude = residue * (8 + settings.intensity * 18 + settings.chaos * 24);
      const flash = this._computeReverseFlash(progress, settings, 'in');
      const reverseFactor = 1 - (flash.pulse * 2.15);
      const warpedAmplitude = amplitude * reverseFactor;
      const reverseDrift = flash.drift;

      return {
        canvasOpacity: 1,
        baseAlpha: 1,
        baseScale: 1.03 - (clear * 0.03),
        baseX: (residue * (4 + settings.chaos * 8) * -1) + reverseDrift,
        baseY: residue * (2 + settings.chaos * 5),
        primary: {
          amplitude: warpedAmplitude,
          lift: (residue * (4 + settings.chaos * 8)) - (reverseDrift * 0.36),
          scale: residue * (0.04 + settings.intensity * 0.05),
          alpha: residue * (0.18 + settings.intensity * 0.12),
          phase: 0.8 + (progress * (3.6 + settings.chaos * 4.4))
        },
        secondary: {
          amplitude: amplitude * 0.52 * (1 - (flash.pulse * 1.95)),
          lift: (drift * residue * (2 + settings.chaos * 4)) + (reverseDrift * 0.24),
          scale: residue * (0.03 + settings.intensity * 0.04),
          alpha: residue * (0.08 + settings.chaos * 0.10),
          phase: 1.2 + (progress * (4.4 + settings.chaos * 5.4))
        },
        echoOpacity: residue * (0.14 + settings.intensity * 0.12),
        prismOpacity: residue * (0.12 + settings.intensity * 0.12 + settings.chaos * 0.08),
        echoX: (warpedAmplitude * -0.14) - (reverseDrift * 0.34),
        echoY: drift * residue * (1.5 + settings.chaos * 3),
        prismX: (warpedAmplitude * 0.12) + (reverseDrift * 0.44),
        prismY: drift * residue * (1 + settings.chaos * 2.4),
        echoScale: 1.03 - (clear * 0.03),
        prismScale: 1.04 - (clear * 0.04),
        echoRotate: residue * (0.10 + settings.chaos * 0.26),
        prismRotate: (residue * (0.08 + settings.chaos * 0.22)) + flash.rotate,
        prismSplit: residue * (1 + settings.intensity * 1.8 + settings.chaos * 3) + (flash.pulse * (1.6 + settings.chaos * 2.6)),
        blur: residue * settings.blur * 0.7,
        echoSaturate: 1.04 + residue * (0.08 + settings.intensity * 0.12),
        prismSaturate: 1.10 + residue * (0.10 + settings.intensity * 0.14),
        flashOpacity: flash.opacity * 0.9,
        flashScale: flash.scale,
        flashRotate: flash.rotate,
        veilOpacity: residue * (0.08 + settings.intensity * 0.08) + (flash.opacity * 0.55),
        grainOpacity: residue * (0.06 + settings.chaos * 0.05) * Math.max(arc, 0.2)
      };
    }

    _drawFrame(job, state) {
      if (!this.ctx) return;
      this.ctx.clearRect(0, 0, this._viewWidth, this._viewHeight);
      this._applyCssState(state);

      const drawable = this._getDrawable(job.source, job.textureUrl);
      if (!drawable) return;

      const crop = this._getCoverCrop(drawable, this._viewWidth, this._viewHeight);
      this._drawBase(drawable, crop, state);
      this._drawSlices(drawable, crop, job.strips.vertical, state.primary, true);
      this._drawSlices(drawable, crop, job.strips.horizontal, state.secondary, false);
    }

    _play(config) {
      if (!this.overlay) return;
      if (this._reducedMotion) {
        config.onPeak?.();
        return;
      }

      this._cleanup();
      this.overlay.classList.add('is-active');
      this._resizeCanvas();

      const settings = resolveSettings(config);
      const duration = config.mode === 'in'
        ? Math.max(650, Math.round(settings.duration * 0.72))
        : settings.duration;

      const job = {
        mode: config.mode,
        source: config.source || this._getCurrentRoot(),
        textureUrl: config.textureUrl || '',
        settings,
        duration,
        peakAt: config.mode === 'out' ? 0.76 : 1,
        start: performance.now(),
        peaked: false,
        strips: this._buildStrips(settings, Date.now() ^ Math.round(settings.chaos * 1000)),
        onPeak: config.onPeak || null
      };

      this._job = job;
      this._setTextureLayers(job.textureUrl);

      const frame = now => {
        if (this._job !== job) return;
        const progress = clamp((now - job.start) / Math.max(job.duration, 1), 0, 1);
        const state = job.mode === 'out'
          ? this._computeOutgoing(progress, job.settings)
          : this._computeIncoming(progress, job.settings);

        this._drawFrame(job, state);

        if (job.mode === 'out' && !job.peaked && progress >= job.peakAt) {
          job.peaked = true;
          job.onPeak?.();
          return;
        }

        if (progress >= 1) {
          if (job.mode === 'in') {
            global.setTimeout(() => this._cleanup(), 120);
          } else {
            this._cleanup();
          }
          return;
        }

        this._raf = global.requestAnimationFrame(frame);
      };

      this._raf = global.requestAnimationFrame(frame);
    }

    _playIncoming() {
      if (!this.overlay) return;
      let payload = null;
      try {
        payload = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      } catch (_) {
        payload = null;
      }
      if (!payload) return;
      sessionStorage.removeItem(STORAGE_KEY);
      if (Date.now() - payload.ts > MAX_AGE_MS) return;
      if (payload.target !== normalizeUrl(global.location.href)) return;

      const root = this._getCurrentRoot();
      const textureUrl = this._extractTexture(root);
      this._play({
        mode: 'in',
        source: root,
        textureUrl,
        duration: payload.duration,
        intensity: payload.intensity,
        chaos: payload.chaos,
        blur: payload.blur,
        reverseFlash: payload.reverseFlash,
        flashIntensity: payload.flashIntensity,
        flashCount: payload.flashCount
      });
    }

    navigate(href, options = {}) {
      const target = new URL(href, global.location.href).toString();
      if (this._reducedMotion || !this.overlay) {
        global.location.href = target;
        return;
      }

      const settings = resolveSettings(options);
      const source = options.source || this._getCurrentRoot();
      const textureUrl = options.textureUrl || this._extractTexture(source);

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          target: normalizeUrl(target),
          duration: settings.duration,
          intensity: settings.intensity,
          chaos: settings.chaos,
          blur: settings.blur,
          reverseFlash: settings.reverseFlash,
          flashIntensity: settings.flashIntensity,
          flashCount: settings.flashCount,
          ts: Date.now()
        }));
      } catch (_) {}

      this._play({
        mode: 'out',
        source,
        textureUrl,
        duration: settings.duration,
        intensity: settings.intensity,
        chaos: settings.chaos,
        blur: settings.blur,
        reverseFlash: settings.reverseFlash,
        flashIntensity: settings.flashIntensity,
        flashCount: settings.flashCount,
        onPeak: () => { global.location.href = target; }
      });
    }
  }

  function boot() {
    global.NoxPageTransition = new NoxPageTransition();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})(window);
